import React, { useMemo, useState, useCallback } from 'react';
import { NSDashboard } from '../../types/interfaces/NSDashborad';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { themeColors } from '../../styles/themeColors';
import useApi from '../../utils/useApi';
import { endpoints } from '../../utils/endpoints';
import CustomFunnel from './components/CustomFunnel/CustomFunnel';
import tPaths from '../../utils/tPaths';
import { useTranslation } from 'react-i18next';
import Chart from './components/Chart/Chart';
import DatePicker, { IDatePickerProps } from '../../components/DatePicker/DatePicker';
import { differenceInCalendarDays, format } from 'date-fns';
import { getPastDays } from '../../utils/dateUtils';

const combineData = (prev: NSDashboard.IActiveUser[], selected: NSDashboard.IActiveUser[]) =>
  prev
    .filter((v, idx) => selected[idx])
    .map((value, index) => {
      return {
        name: format(new Date(selected[index].date), 'MM-dd'),
        previous: value.activeUsers,
        selected: selected[index].activeUsers,
      };
    });

const Dashboard = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState({
    startDate: getPastDays(30),
    endDate: getPastDays(1),
  });

  const { data: appStats } = useApi<NSDashboard.IAppStats>(
    endpoints.appStats(dateRange.startDate.toUTCString(), dateRange.endDate.toUTCString()),
  );
  const prevRange = useMemo(
    () => ({
      startDate: getPastDays(
        -differenceInCalendarDays(dateRange.startDate, dateRange.endDate),
        dateRange.startDate,
      ),
      endDate: getPastDays(1, dateRange.startDate),
    }),
    [dateRange.endDate, dateRange.startDate],
  );

  // const { data: appStats } = useApi<NSDashboard.IAppStats>(
  //   endpoints.appStats(dateRange.startDate.toUTCString(), dateRange.endDate.toUTCString()),
  // );
  const { data: prevActiveUsers, isDataValid: isPrevActiveUsersValid } =
    useApi<NSDashboard.IActiveUsersResponse>(
      endpoints.activeUsers(prevRange.startDate.toUTCString(), prevRange.endDate.toUTCString()),
    );

  const { data: currentActiveUsers, isDataValid: isCurrentActiveUsersValid } =
    useApi<NSDashboard.IActiveUsersResponse>(
      endpoints.activeUsers(dateRange.startDate.toUTCString(), dateRange.endDate.toUTCString()),
    );

  const chartData = useMemo(
    () =>
      isPrevActiveUsersValid && isCurrentActiveUsersValid && prevActiveUsers && currentActiveUsers
        ? combineData(prevActiveUsers.activeUsers, currentActiveUsers.activeUsers)
        : [],
    [currentActiveUsers, isCurrentActiveUsersValid, isPrevActiveUsersValid, prevActiveUsers],
  );

  const funnelData = useMemo(
    () =>
      appStats && [
        {
          name: t(tPaths.dashboard.downloads),
          // funnel library crashes if all values are 0
          value: appStats.downloads || 1,
          fill: themeColors.funnel.downloads,
        },
        {
          name: t(tPaths.dashboard.withESim),
          value: appStats.withESim,
          fill: themeColors.funnel.withESim,
        },
        {
          name: t(tPaths.dashboard.loggedIn),
          value: appStats.loggedIn,
          fill: themeColors.funnel.loggedIn,
        },

        {
          name: t(tPaths.dashboard.activeESimProfile),
          value: appStats.activeESimProfile,
          fill: themeColors.funnel.activeESimProfile,
        },
      ],
    [appStats, t],
  );

  const handleDateChange: IDatePickerProps['onChange'] = useCallback(
    (selectedRange) => setDateRange(selectedRange),
    [],
  );

  return useMemo(
    () => (
      <>
        <Box display="flex" margin={2} alignItems="center">
          <Box marginRight={2}>
            <Typography variant="h5">Reports for:</Typography>
          </Box>
          <DatePicker range={dateRange} onChange={handleDateChange} />
        </Box>
        <Box display="flex">
          {<Chart data={chartData} title={t(tPaths.dashboard.activeUsersChartTitle)} />}
          {funnelData ? <CustomFunnel data={funnelData} /> : <CircularProgress />}
        </Box>
      </>
    ),
    [chartData, dateRange, funnelData, handleDateChange, t],
  );
};

export default Dashboard;
